import type { EntityListResponse } from "@/types/entity";

// The MediaMath API hard-caps page_limit at 1000 (confirmed via its own
// "page_limit is out of range (1 - 1000)" error) — there is no way to
// request more in a single call, so returning a full list means paging
// through with page_offset until total_count is reached.
export const MAX_PAGE_LIMIT = 1000;

// nginx's default max URI is 8 KB. 100 six-digit IDs ≈ 700 bytes, leaving
// ample room for the base URL and other query parameters.
export const MAX_IDS_PER_QUERY = 100;

export const chunkArray = <T>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, (i + 1) * size)
  );

// One page after another, each starting where the last one ended. This is the
// exact path: it never assumes a page size, so it is also what the parallel path
// falls back to whenever the API's numbers don't add up.
const fetchSequentially = async <T>(
  fetchPage: (pageOffset: number) => Promise<EntityListResponse<T>>
): Promise<T[]> => {
  const results: T[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchPage(offset);
    results.push(...response.data);
    offset += response.data.length;
    hasMore = response.data.length > 0 && offset < response.meta.total_count;
  }

  return results;
};

// Once the first page reports total_count, every other offset is known, so the
// remaining pages go out together instead of one round trip each. The parallel
// path only applies when the first page came back full (MAX_PAGE_LIMIT rows),
// which is what makes a fixed offset step valid. If the pages then don't add up
// to total_count — a short page in the middle, or the list changing while it
// loads — the list is fetched again sequentially, which is always exact.
export const fetchAllPages = async <T>(
  fetchPage: (pageOffset: number) => Promise<EntityListResponse<T>>
): Promise<T[]> => {
  const firstPage = await fetchPage(0);
  const totalCount = firstPage.meta.total_count;

  if (firstPage.data.length >= totalCount) return firstPage.data;
  if (firstPage.data.length < MAX_PAGE_LIMIT)
    return fetchSequentially(fetchPage);

  const remainingOffsets = Array.from(
    { length: Math.ceil(totalCount / MAX_PAGE_LIMIT) - 1 },
    (_, index) => (index + 1) * MAX_PAGE_LIMIT
  );
  const remainingPages = await Promise.all(
    remainingOffsets.map((pageOffset) => fetchPage(pageOffset))
  );
  const results = [firstPage, ...remainingPages].flatMap((page) => page.data);

  return results.length === totalCount ? results : fetchSequentially(fetchPage);
};
